import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrash, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, 
    FaSave, FaBold, FaItalic, FaUnderline, FaListUl, FaListOl, FaHeading, FaImage, FaParagraph,
    FaCalendarAlt, FaMapMarkerAlt, FaLink, FaClock
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';
import './AddEvent.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const formatDateRange = (start, end) => {
    if (!start) return '-';
    const s = new Date(start).toLocaleDateString('en-IN', { dateStyle: 'medium' });
    if (!end || start.split('T')[0] === end.split('T')[0]) return s;
    const e = new Date(end).toLocaleDateString('en-IN', { dateStyle: 'medium' });
    return `${s} to ${e}`;
};

const formatTimeAmPm = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hours = parseInt(h, 10);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hr12 = hours % 12 || 12;
    return `${hr12.toString().padStart(2, '0')}:${m} ${suffix}`;
};

const formatDateTimeString = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

// --- CUSTOM RICH TEXT EDITOR BLOCK ---
const TextEditorBlock = ({ block, onChange }) => {
    const editorRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (editorRef.current && !isFocused && block.content !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = block.content;
        }
    }, [block.content, isFocused]);

    const formatDoc = (cmd, value = null) => {
        document.execCommand(cmd, false, value);
        editorRef.current.focus();
        onChange(editorRef.current.innerHTML);
    };

    return (
        <div className="admin-add-event-editor-container">
            <div className="admin-add-event-editor-toolbar">
                <button title="Bold" onClick={() => formatDoc('bold')}><FaBold /></button>
                <button title="Italic" onClick={() => formatDoc('italic')}><FaItalic /></button>
                <button title="Underline" onClick={() => formatDoc('underline')}><FaUnderline /></button>
                <div className="admin-add-event-toolbar-divider"></div>
                <button title="Heading 1" onClick={() => formatDoc('formatBlock', 'H1')}><FaHeading />1</button>
                <button title="Heading 2" onClick={() => formatDoc('formatBlock', 'H2')}><FaHeading />2</button>
                <button title="Paragraph" onClick={() => formatDoc('formatBlock', 'P')}><FaParagraph /></button>
                <div className="admin-add-event-toolbar-divider"></div>
                <button title="Unordered List" onClick={() => formatDoc('insertUnorderedList')}><FaListUl /></button>
                <button title="Ordered List" onClick={() => formatDoc('insertOrderedList')}><FaListOl /></button>
            </div>
            <div 
                className="admin-add-event-editor-content" 
                contentEditable={true} 
                suppressContentEditableWarning={true}
                ref={editorRef}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onInput={(e) => onChange(e.currentTarget.innerHTML)}
            />
        </div>
    );
};

// --- MULTIPLE IMAGE GALLERY BLOCK ---
const ImageGalleryBlock = ({ block, onChange }) => {
    const existingImages = block.imageUrls || [];
    const newFiles = block.files || [];

    const handleFiles = (e) => {
        const files = Array.from(e.target.files);
        onChange({ files: [...newFiles, ...files], imageUrls: existingImages });
    };

    const removeExistingImage = (index) => {
        const updated = [...existingImages];
        updated.splice(index, 1);
        onChange({ files: newFiles, imageUrls: updated });
    };

    const removeNewFile = (index) => {
        const updated = [...newFiles];
        updated.splice(index, 1);
        onChange({ files: updated, imageUrls: existingImages });
    };

    const totalCount = existingImages.length + newFiles.length;

    return (
        <div className="admin-add-event-gallery-container">
            <div className="admin-add-event-gallery-upload-zone">
                <input type="file" multiple accept="image/*" onChange={handleFiles} id={`gallery-upload-${block.id}`} hidden />
                <label htmlFor={`gallery-upload-${block.id}`} className="admin-add-event-upload-label">
                    <FaUpload />
                    <span>Click to select multiple images</span>
                </label>
            </div>

            {totalCount > 0 && (
                <div className="admin-add-event-gallery-preview" data-count={totalCount}>
                    {existingImages.map((img, idx) => (
                        <div key={`old-${idx}`} className="admin-add-event-gallery-item">
                            <img src={`${API_BASE_URL}${img}`} alt="" />
                            <button type="button" className="admin-add-event-gallery-remove" onClick={() => removeExistingImage(idx)}><FaTimes /></button>
                        </div>
                    ))}
                    {newFiles.map((file, idx) => (
                        <div key={`new-${idx}`} className="admin-add-event-gallery-item">
                            <img src={URL.createObjectURL(file)} alt="" />
                            <button type="button" className="admin-add-event-gallery-remove" onClick={() => removeNewFile(idx)}><FaTimes /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const initialFormState = { title: '', slug: '', eventDate: '', endDate: '', startTime: '', endTime: '', eventPlace: '', isActive: true };

const AddEvent = () => {
    const [eventData, setEventData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); 
    
    const [showAddForm, setShowAddForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [slugEdited, setSlugEdited] = useState(false);
    const [blocks, setBlocks] = useState([{ id: Date.now(), type: 'text', content: '', files: [] }]);

    useEffect(() => { fetchEvents(); }, []);

    const fetchEvents = async () => {
        try {
            const res = await axiosInstance.get('/api/events');
            setEventData(res.data);
        } catch (error) { console.error("Error fetching events:", error); }
    };

    const filteredData = useMemo(() => eventData.filter(item => 
        (item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.eventPlace?.toLowerCase().includes(searchTerm.toLowerCase()))
    ), [eventData, searchTerm]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    const handleTitleChange = (e) => {
        const title = e.target.value;
        let newSlug = formData.slug;
        if (!slugEdited) newSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
        setFormData({ ...formData, title, slug: newSlug });
    };

    const addBlock = (type) => setBlocks([...blocks, { id: Date.now(), type, content: '', files: [] }]);
    
    const updateBlock = (id, field, value) => {
        setBlocks((prev) => prev.map((block) => {
            if (block.id !== id) return block;
            if (typeof value === "object") return { ...block, ...value };
            return { ...block, [field]: value };
        }));
    };
    
    const removeBlock = (id) => setBlocks(blocks.filter(b => b.id !== id));
    
    const moveBlock = (index, dir) => {
        if (index + dir < 0 || index + dir >= blocks.length) return;
        const newBlocks = [...blocks];
        [newBlocks[index], newBlocks[index + dir]] = [newBlocks[index + dir], newBlocks[index]];
        setBlocks(newBlocks);
    };

    const handleSave = async () => {
        if (!formData.title) return Swal.fire('Error', 'Please enter event title', 'error');
        if (!formData.slug) return Swal.fire('Error', 'Please enter a slug URL', 'error');
        if (!formData.eventDate) return Swal.fire('Error', 'Please select event start date', 'error');
        if (!formData.eventPlace || formData.eventPlace.trim() === '') return Swal.fire('Error', 'Please enter event place', 'error');
        
        const hasText = blocks.some(b => b.type === 'text' && b.content && b.content.replace(/<[^>]*>?/gm, '').trim() !== '');
        if (!hasText) return Swal.fire('Error', 'At least one text block with text content is required.', 'error');
        
        const submissionData = new FormData();
        submissionData.append('title', formData.title); 
        submissionData.append('slug', formData.slug); 
        submissionData.append('eventDate', formData.eventDate); 
        submissionData.append('endDate', formData.endDate || ''); 
        submissionData.append('startTime', formData.startTime || ''); 
        submissionData.append('endTime', formData.endTime || ''); 
        submissionData.append('eventPlace', formData.eventPlace); 
        submissionData.append('isActive', formData.isActive);

        const cleanedBlocks = blocks.map((b) => ({
            id: b.id, type: b.type,
            content: b.type === "text" ? b.content : "",
            imageUrls: b.type === "image" ? (b.imageUrls || []) : []
        }));

        submissionData.append('contentBlocks', JSON.stringify(cleanedBlocks));
        
        blocks.forEach((b) => {
            if (b.type === 'image' && b.files) {
                b.files.forEach((f) => submissionData.append(`block_${b.id}_images`, f));
            }
        });

        const url = editId ? `/api/events/${editId}` : `/api/events`;
        const method = editId ? 'put' : 'post';

        try {
            await axiosInstance({ method, url, data: submissionData });
            await fetchEvents();
            Swal.fire(editId ? 'Updated!' : 'Published!', '', 'success');
            resetForm();
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Action failed! Please try again.';
            Swal.fire('Error', errMsg, 'error');
        }
    };

    const handleEdit = (data) => {
        setFormData({ 
            title: data.title, 
            slug: data.slug || '', 
            eventDate: data.eventDate ? data.eventDate.split('T')[0] : '', 
            endDate: data.endDate ? data.endDate.split('T')[0] : '', 
            startTime: data.startTime || '',
            endTime: data.endTime || '',
            eventPlace: data.eventPlace || '',
            isActive: data.isActive !== false
        });
        if (data.contentBlocks) {
            try { setBlocks(JSON.parse(data.contentBlocks)); } 
            catch(e) { setBlocks([{ id: Date.now(), type: 'text', content: data.content, files: [] }]); }
        } else {
            setBlocks([{ id: Date.now(), type: 'text', content: '', files: [] }]);
        }
        setSlugEdited(true);
        setEditId(data._id);
        setShowAddForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' }); 
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete Event?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/events/${id}`);
                    await fetchEvents();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    Swal.fire('Error', 'Could not delete event.', 'error');
                }
            }
        });
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/api/events/${id}/status`, { isActive: !currentStatus });
            fetchEvents();
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setFormData(initialFormState);
        setBlocks([{ id: Date.now(), type: 'text', content: '', files: [] }]);
        setSlugEdited(false);
        setEditId(null);
    };

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));

    return (
        <>
        <Helmet>
                    <title>Events | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage Events for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="admin-add-event-container">
            <h2 className="admin-add-event-title"><FaCalendarAlt style={{marginRight: '8px'}}/> Event Management</h2>
            
            <div className="admin-add-event-topbar">
                <div className="admin-add-event-topbar-left">
                    <div className="admin-add-event-search-wrapper">
                        <FaSearch className="admin-add-event-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by title or place..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="admin-add-event-search-input"
                        />
                    </div>
                </div>

                <div className="admin-add-event-topbar-right">
                    <select
                        value={itemsPerPage}
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="admin-add-event-items-select"
                    >
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="20">20 / page</option>
                        <option value="30">30 / page</option>
                        <option value="50">50 / page</option>
                    </select>

                    <button
                        className="admin-add-event-btn admin-add-event-btn-add"
                        onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}
                    >
                        {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Create Event</>}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <div className="admin-add-event-inline-form">
                    <h3 className="admin-add-event-form-title">{editId ? '✏️ Edit Event' : 'Create New Event'}</h3>
                    
                    <div className="admin-add-event-layout-grid">
                        <div className="admin-add-event-sidebar">
                            <h4 className="admin-add-event-section-title">Event Details</h4>
                            <div className="admin-add-event-form-group">
                                <label>Event Title <span className="admin-add-event-req">*</span></label>
                                <input type="text" value={formData.title} onChange={handleTitleChange} />
                            </div>
                            <div className="admin-add-event-form-group">
                                <label>Slug (URL) <span className="admin-add-event-req">*</span></label>
                                <input type="text" value={formData.slug} onChange={e => {setSlugEdited(true); setFormData({...formData, slug: e.target.value});}} style={{fontFamily: 'monospace', color: '#8b5cf6'}}/>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
                                <div className="admin-add-event-form-group">
                                    <label>Start Date <span className="admin-add-event-req">*</span></label>
                                    <input type="date" value={formData.eventDate} onChange={(e)=>setFormData({...formData, eventDate: e.target.value})} />
                                </div>
                                <div className="admin-add-event-form-group">
                                    <label>Start Time</label>
                                    <input type="time" value={formData.startTime} onChange={(e)=>setFormData({...formData, startTime: e.target.value})} />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
                                <div className="admin-add-event-form-group">
                                    <label>End Date</label>
                                    <input type="date" value={formData.endDate} onChange={(e)=>setFormData({...formData, endDate: e.target.value})} />
                                </div>
                                <div className="admin-add-event-form-group">
                                    <label>End Time</label>
                                    <input type="time" value={formData.endTime} onChange={(e)=>setFormData({...formData, endTime: e.target.value})} />
                                </div>
                            </div>

                            <div className="admin-add-event-form-group">
                                <label>Event Place <span className="admin-add-event-req">*</span></label>
                                <input type="text" value={formData.eventPlace} onChange={(e)=>setFormData({...formData, eventPlace: e.target.value})} placeholder="e.g. Main Auditorium"/>
                            </div>
                        </div>

                        <div className="admin-add-event-main-area">
                            <h4 className="admin-add-event-section-title">Content Blocks</h4>
                            <div className="admin-add-event-blocks-wrapper">
                                {blocks.map((block, idx) => (
                                    <div key={block.id} className="admin-add-event-block-card">
                                        <div className="admin-add-event-block-header">
                                            <span className="admin-add-event-block-badge">
                                                {block.type === 'text' ? <FaParagraph /> : <FaImage />} {block.type.toUpperCase()}
                                            </span>
                                            <div className="admin-add-event-block-actions">
                                                <button onClick={() => moveBlock(idx, -1)} disabled={idx === 0}><FaChevronUp /></button>
                                                <button onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1}><FaChevronDown /></button>
                                                <button onClick={() => removeBlock(block.id)} className="delete"><FaTrash /></button>
                                            </div>
                                        </div>
                                        <div className="admin-add-event-block-body">
                                            {block.type === 'text' && <TextEditorBlock block={block} onChange={(val) => updateBlock(block.id, 'content', val)} />}
                                            {block.type === 'image' && <ImageGalleryBlock block={block} onChange={(val) => updateBlock(block.id, null, val)} />}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="admin-add-event-add-blocks-row">
                                <button className="admin-add-event-add-block-btn text" onClick={() => addBlock('text')}>+ Add Text Block</button>
                                <button className="admin-add-event-add-block-btn image" onClick={() => addBlock('image')}>+ Add Image Gallery</button>
                            </div>
                        </div>
                    </div>

                    <div className="admin-add-event-form-buttons">
                        <button className="admin-add-event-btn-cancel" onClick={resetForm}>Cancel</button>
                        <button className="admin-add-event-btn-confirm" onClick={handleSave}><FaSave style={{marginRight: '5px'}}/> {editId ? 'Update Event' : 'Publish Event'}</button>
                    </div>
                </div>
            )}

            <div className="admin-add-event-table-wrapper">
                <table className="admin-add-event-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th className="admin-add-event-align-left">Event Title</th>
                            <th className="admin-add-event-col-slug">Slug</th>
                            <th className="admin-add-event-col-date">Date</th>
                            <th className="admin-add-event-col-place">Place</th>
                            <th className="admin-add-event-col-status">Status</th>
                            <th className="admin-add-event-col-action">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 ? (
                            <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No events found.</td></tr>
                        ) : paginatedData.map((item, index) => (
                            <React.Fragment key={item._id}>
                                <tr className="admin-add-event-main-row">
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    
                                    <td className="admin-add-event-align-left">
                                        <div className="admin-add-event-name-wrapper" onClick={() => toggleRow(item._id)}>
                                            <span style={{ fontWeight: 600 }}>{item.title}</span>
                                            <button className="admin-add-event-expand-btn">
                                                {expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                        </div>
                                    </td>

                                    <td className="admin-add-event-col-slug">
                                        <div className="admin-add-event-slug-badge"><FaLink className="slug-icon"/> {item.slug}</div>
                                    </td>

                                    <td className="admin-add-event-col-date">{formatDateRange(item.eventDate, item.endDate)}</td>
                                    <td className="admin-add-event-col-place">{item.eventPlace || '—'}</td>
                                    
                                    <td className="admin-add-event-col-status">
                                        <label className="admin-add-event-switch">
                                            <input type="checkbox" checked={item.isActive !== false} onChange={() => toggleStatus(item._id, item.isActive !== false)} />
                                            <span className="admin-add-event-slider" />
                                        </label>
                                        <div style={{ fontSize: '0.75rem', marginTop: '4px', color: item.isActive !== false ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                                            {item.isActive !== false ? 'Active' : 'Draft'}
                                        </div>
                                    </td>

                                    <td className="admin-add-event-col-action">
                                        <div className="admin-add-event-action-btns">
                                            <button className="admin-add-event-btn-edit" onClick={() => handleEdit(item)} title="Edit"><FaEdit /></button>
                                            <button className="admin-add-event-btn-delete" onClick={() => handleDelete(item._id)} title="Delete"><FaTrash /></button>
                                        </div>
                                    </td>
                                </tr>
                                
                                {expandedRow === item._id && (
                                    <tr className="admin-add-event-expanded-row">
                                        <td colSpan="7">
                                            <div className="admin-add-event-expanded-content">
                                                <div className="admin-add-event-exp-item admin-add-event-exp-slug">
                                                    <span className="admin-add-event-exp-label">Slug URL</span>
                                                    <span className="admin-add-event-exp-value admin-add-event-slug-badge"><FaLink className="slug-icon"/> {item.slug}</span>
                                                </div>
                                                <div className="admin-add-event-exp-item admin-add-event-exp-date">
                                                    <span className="admin-add-event-exp-label">Event Date</span>
                                                    <span className="admin-add-event-exp-value">{formatDateRange(item.eventDate, item.endDate)}</span>
                                                </div>
                                                <div className="admin-add-event-exp-item admin-add-event-exp-place">
                                                    <span className="admin-add-event-exp-label">Time</span>
                                                    <span className="admin-add-event-exp-value">
                                                        {item.startTime ? `${formatTimeAmPm(item.startTime)} ${item.endTime ? `- ${formatTimeAmPm(item.endTime)}` : ''}` : '—'}
                                                    </span>
                                                </div>
                                                <div className="admin-add-event-exp-item admin-add-event-exp-place">
                                                    <span className="admin-add-event-exp-label">Event Place</span>
                                                    <span className="admin-add-event-exp-value">{item.eventPlace || '—'}</span>
                                                </div>
                                                <div className="admin-add-event-exp-item">
                                                    <span className="admin-add-event-exp-label">Created On</span>
                                                    <span className="admin-add-event-exp-value">{formatDateTimeString(item.createdAt)}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination 
                totalItems={filteredData.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
            />
        </div>
        </>
    );
};

export default AddEvent;